import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import katex from "katex";

/**
 * Render question text (with LaTeX) to HTML
 */
function renderQuestionHTML(text) {
  if (!text) return "";
  let processed = text.replace(/\\n/g, "\n");
  processed = processed
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, m) => `$$${m.trim()}$$`)
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, m) => `$${m.trim()}$`);

  const parts = [];
  const blockSplit = processed.split(/(\$\$[\s\S]*?\$\$)/g);
  blockSplit.forEach((segment) => {
    if (segment.startsWith("$$") && segment.endsWith("$$")) {
      const math = segment.slice(2, -2).trim();
      try { parts.push(katex.renderToString(math, { displayMode: true, throwOnError: false })); }
      catch { parts.push(`<div style="font-family:monospace">${math}</div>`); }
    } else {
      segment.split(/(\$[^$\n]+?\$)/g).forEach((seg) => {
        if (seg.startsWith("$") && seg.endsWith("$") && seg.length > 2) {
          const math = seg.slice(1, -1).trim();
          try { parts.push(katex.renderToString(math, { displayMode: false, throwOnError: false })); }
          catch { parts.push(`<span style="font-family:monospace">${math}</span>`); }
        } else {
          parts.push(seg.replace(/\n/g, "<br/>"));
        }
      });
    }
  });
  return parts.join("");
}

/** Load image as base64 */
function loadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/**
 * Modern Question Sheet PDF
 */
export async function downloadQuestionSheet(questionText) {
  const PW = 210, PH = 297, M = 12;
  const CW = PW - M * 2;

  // Load logos
  let logoLeft = null, logoRight = null;
  try {
    [logoLeft, logoRight] = await Promise.all([
      loadImage("/lokesh-sir-logo.jpg"),
      loadImage("/descriptive-test-logo.png"),
    ]);
  } catch { /* continue without */ }

  // Render question to image (white bg, dark text, small font)
  const qDiv = document.createElement("div");
  qDiv.style.cssText = `
    position:fixed; left:-9999px; top:0; z-index:-1;
    width:620px; padding:10px 14px;
    font-family:'Inter','Segoe UI',Arial,sans-serif; font-size:11.5px;
    line-height:1.5; color:#1e293b; background:#ffffff;
  `;
  qDiv.innerHTML = `
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css">
    <div style="font-size:11.5px;line-height:1.5;color:#1e293b;word-wrap:break-word;">
      ${renderQuestionHTML(questionText)}
    </div>
  `;
  document.body.appendChild(qDiv);
  await new Promise((r) => setTimeout(r, 400));

  try {
    const qCanvas = await html2canvas(qDiv, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false });
    const qImg = qCanvas.toDataURL("image/png");
    const qW = CW - 10;
    const qH = qW / (qCanvas.width / qCanvas.height);

    const pdf = new jsPDF("portrait", "mm", "a4");
    let y = 0;

    // =============== HEADER BAND ===============
    // Full-width dark navy header
    const bandH = 22;
    pdf.setFillColor(15, 23, 42); // slate-900
    pdf.rect(0, 0, PW, bandH, "F");

    // Left logo: Lokesh Sir Notes (white on black - fits dark header)
    if (logoLeft) {
      pdf.addImage(logoLeft, "JPEG", M + 1, 2.5, 17, 17, undefined, "FAST");
    }
    // Text next to left logo
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.text("LOKESH SIR NOTES", M + 20, 9);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(5);
    pdf.setTextColor(148, 163, 184);
    pdf.text("QUALITY AT IT'S PEAK", M + 20, 13);

    // Center title
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bolditalic");
    pdf.setFontSize(11);
    pdf.text("Question Sheet", PW / 2, 10, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6);
    pdf.setTextColor(148, 163, 184);
    pdf.text("by Lokesh Sir", PW / 2, 14.5, { align: "center" });

    // Right logo: Descriptive Test
    if (logoRight) {
      pdf.addImage(logoRight, "PNG", PW - M - 32, 4, 30, 14, undefined, "FAST");
    }

    // Blue accent line under header
    pdf.setFillColor(59, 130, 246); // blue-500
    pdf.rect(0, bandH, PW, 1.2, "F");
    y = bandH + 1.2;

    // =============== INFO BAR ===============
    y += 2;
    pdf.setFillColor(241, 245, 249); // slate-100
    pdf.rect(M, y, CW, 7, "F");
    // Left: website
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.5);
    pdf.setTextColor(59, 130, 246);
    pdf.text("descriptivetest.com/enroll", M + 3, y + 4.5);
    // Right: brand
    pdf.setTextColor(51, 65, 85); // slate-700
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.5);
    pdf.text("Lokesh Sir Notes", PW - M - 3, y + 4.5, { align: "right" });
    y += 10;

    // =============== QUESTION SECTION ===============
    // "QUESTION" label with blue pill
    pdf.setFillColor(59, 130, 246);
    pdf.roundedRect(M, y, 24, 6, 3, 3, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    pdf.text("QUESTION", M + 12, y + 4, { align: "center" });

    // 4 MARKS badge on right
    pdf.setFillColor(234, 179, 8); // amber-500
    pdf.roundedRect(PW - M - 18, y, 18, 6, 3, 3, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6);
    pdf.text("4 MARKS", PW - M - 9, y + 4, { align: "center" });
    y += 9;

    // Question card — white with left blue accent bar + subtle border
    const maxQH = PH * 0.2;
    const actualQH = Math.min(qH, maxQH);
    const cardH = actualQH + 8;

    // Card border
    pdf.setDrawColor(226, 232, 240); // slate-200
    pdf.setLineWidth(0.3);
    pdf.roundedRect(M, y, CW, cardH, 2, 2, "S");

    // Left blue accent bar
    pdf.setFillColor(59, 130, 246);
    pdf.rect(M, y, 2.5, cardH, "F");

    // Question image (white bg, dark text)
    pdf.addImage(qImg, "PNG", M + 5, y + 4, qW - 2, actualQH, undefined, "FAST");
    y += cardH + 5;

    // =============== ANSWER AREA (KEPT SAME) ===============
    const ansH = PH - M - y - 6; // leave space for footer

    // Dark green rounded border
    pdf.setDrawColor(22, 101, 52); // green-800
    pdf.setLineWidth(0.8);
    pdf.roundedRect(M, y, CW, ansH, 5, 5, "S");

    // Teal lines inside
    const pad = 5;
    const lx1 = M + pad, lx2 = PW - M - pad;
    let ly = y + 13;
    pdf.setDrawColor(20, 120, 140);
    pdf.setLineWidth(0.25);
    while (ly < y + ansH - pad) {
      pdf.line(lx1, ly, lx2, ly);
      ly += 13;
    }

    // =============== FOOTER ===============
    const footY = PH - 5;
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.2);
    pdf.line(M, footY - 2, PW - M, footY - 2);
    pdf.setFontSize(5);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(148, 163, 184);
    pdf.text("descriptivetest.com", M, footY);
    pdf.text("Lokesh Sir Notes  |  Quality at it's peak", PW / 2, footY, { align: "center" });
    pdf.text("Question Sheet", PW - M, footY, { align: "right" });

    // Save
    const safe = (questionText || "q").slice(0, 40).replace(/[^a-zA-Z0-9 ]/g, "").trim();
    pdf.save(`Question_Sheet_${safe}.pdf`);
  } finally {
    document.body.removeChild(qDiv);
  }
}
